/**
 * NameInput Component
 * Reusable name input with validation for auth and guest forms
 */

import { cn } from '@/lib/utils';
import { SPACING } from '@/design-system';
import { Label } from '@/components/ui/label';
import { ValidatedInput } from '@/components/FormValidation';
import { LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';

interface NameInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  compact?: boolean;
  hideLabel?: boolean;
}

export function NameInput({
  id = 'name',
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  required = false,
  label,
  placeholder,
  compact = false,
  hideLabel = false,
}: NameInputProps) {
  const { lComponents } = useLocalizations();

  return (
    <div className={compact ? 'space-y-1' : SPACING.formFieldGap}>
      {!hideLabel && (
        <Label htmlFor={id} className={compact ? 'text-sm' : undefined}>
          {label || <LCommon id="labels.name" defaultValue="Name" />}
          {!required && <span className="text-muted-foreground text-xs ml-1">(optional)</span>}
        </Label>
      )}
      <ValidatedInput
        id={id}
        type="text"
        placeholder={placeholder || lComponents('compose_message.guest.name_placeholder', { defaultValue: 'Enter your name' })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        error={error}
        required={required}
        className={compact ? 'h-9' : undefined}
      />
    </div>
  );
}
